/**
 * 50 space badges — progression achievements (serious + silly).
 * Icons are emoji/glyph for zero-asset unlock toasts and gallery.
 */

/** @typedef {{ id: string, name: string, desc: string, icon: string, tier: 'bronze'|'silver'|'gold'|'legend', funny?: boolean, check: (s: object) => boolean }} BadgeDef */

/** @type {BadgeDef[]} */
export const BADGE_DEFS = [
    // ── Combat ──
    {
        id: 'first_blood', name: 'First Blood', icon: '🩸', tier: 'bronze',
        desc: 'Score your first kill.',
        check: (s) => s.kills >= 1,
    },
    {
        id: 'geometric_violence', name: 'Geometric Violence', icon: '◆', tier: 'silver',
        desc: 'Destroy 100 enemies in one career.',
        check: (s) => s.kills >= 100,
    },
    {
        id: 'thousand_yard', name: 'Thousand-Yard Stare', icon: '👁', tier: 'gold',
        desc: '1,000 lifetime kills. Therapy recommended.',
        check: (s) => s.kills >= 1000,
    },
    {
        id: 'boss_whisperer', name: 'Boss Whisperer', icon: '👑', tier: 'silver',
        desc: 'Defeat your first boss.',
        check: (s) => s.bossKills >= 1,
    },
    {
        id: 'boss_buffet', name: 'Boss Buffet', icon: '🍽️', tier: 'gold',
        desc: 'Defeat 10 bosses. They were delicious.',
        check: (s) => s.bossKills >= 10,
    },
    {
        id: 'ufo_spotter', name: 'UFO Spotter', icon: '🛸', tier: 'bronze', funny: true,
        desc: 'Shoot down a saucer. Definitely not tourists.',
        check: (s) => (s.killsByVariant?.saucer || 0) >= 1,
    },
    {
        id: 'cow_abductor', name: 'Cow Abductor', icon: '🐄', tier: 'silver', funny: true,
        desc: 'Down 10 saucers. The herd is nervous.',
        check: (s) => (s.killsByVariant?.saucer || 0) >= 10,
    },
    {
        id: 'probe_me_maybe', name: 'Probe Me Maybe', icon: '🔬', tier: 'bronze', funny: true,
        desc: 'Get hit by a saucer shot. Awkward.',
        check: (s) => s.hitBySaucer >= 1,
    },
    {
        id: 'minesweeper', name: 'Space Minesweeper', icon: '💣', tier: 'bronze',
        desc: 'Detonate / destroy a space mine enemy.',
        check: (s) => (s.killsByVariant?.mine || 0) >= 1,
    },
    {
        id: 'phantom_menace', name: 'Phantom Menace', icon: '👻', tier: 'silver', funny: true,
        desc: 'Kill a cloaked phantom. Trust no shimmer.',
        check: (s) => (s.killsByVariant?.phantom || 0) >= 1,
    },
    {
        id: 'interceptor_ace', name: 'Interceptor Ace', icon: '⚡', tier: 'silver',
        desc: 'Bag 25 interceptors mid-dash.',
        check: (s) => (s.killsByVariant?.interceptor || 0) >= 25,
    },
    {
        id: 'bomber_holiday', name: 'Bomber Holiday', icon: '📦', tier: 'bronze', funny: true,
        desc: 'Ground a bomber. Packages denied.',
        check: (s) => (s.killsByVariant?.bomber || 0) >= 1,
    },
    // ── Progression / pilot ──
    {
        id: 'fresh_meat', name: 'Fresh Meat', icon: '🥩', tier: 'bronze', funny: true,
        desc: 'Die at least once. Welcome to space.',
        check: (s) => s.deaths >= 1,
    },
    {
        id: 'still_flying', name: 'Still Flying Though', icon: '🩹', tier: 'silver', funny: true,
        desc: 'Survive a hit with 1 HP left.',
        check: (s) => s.nearDeathSurvives >= 1,
    },
    {
        id: 'seasoned_pilot', name: 'Seasoned Pilot', icon: '🎖️', tier: 'silver',
        desc: 'Reach pilot level 5.',
        check: (s) => s.pilotLevel >= 5,
    },
    {
        id: 'ace_pilot', name: 'Ace Pilot', icon: '🏅', tier: 'gold',
        desc: 'Reach pilot level 10.',
        check: (s) => s.pilotLevel >= 10,
    },
    {
        id: 'void_legend', name: 'Legend of the Void', icon: '🌟', tier: 'legend',
        desc: 'Reach pilot level 25.',
        check: (s) => s.pilotLevel >= 25,
    },
    {
        id: 'wave_rider', name: 'Wave Rider', icon: '🌊', tier: 'bronze',
        desc: 'Complete sector level 1.',
        check: (s) => s.sectorsCleared >= 1,
    },
    {
        id: 'pocket_change', name: 'Pocket Change', icon: '✦', tier: 'bronze',
        desc: 'Earn 100 crystals lifetime.',
        check: (s) => s.crystalsEarned >= 100,
    },
    {
        id: 'crystal_tycoon', name: 'Crystal Tycoon', icon: '💎', tier: 'gold',
        desc: 'Earn 2,500 crystals lifetime.',
        check: (s) => s.crystalsEarned >= 2500,
    },
    {
        id: 'broke_in_space', name: 'Broke In Space', icon: '🪙', tier: 'bronze', funny: true,
        desc: 'Hit exactly 0 crystals. Relatable.',
        check: (s) => s.timesBroke >= 1,
    },

    // ── Planets / galaxies ──
    {
        id: 'galaxy_hopper', name: 'Galaxy Hopper', icon: '🌌', tier: 'silver',
        desc: 'Hyperjump 5 times.',
        check: (s) => s.hyperjumps >= 5,
    },
    {
        id: 'planet_stamp', name: 'Planet Stamp Collector', icon: '🪐', tier: 'gold',
        desc: 'Visit 12 different planets.',
        check: (s) => (s.planetsVisited?.length || 0) >= 12,
    },
    {
        id: 'desert_rat', name: 'Desert Rat', icon: '🏜️', tier: 'bronze',
        desc: 'Land on a desert world.',
        check: (s) => !!s.planetTypes?.desert,
    },
    {
        id: 'ice_cold', name: 'Ice Cold', icon: '🧊', tier: 'bronze', funny: true,
        desc: 'Touch down on ice. Bring a jacket.',
        check: (s) => !!s.planetTypes?.ice,
    },
    {
        id: 'too_hot', name: 'Too Hot', icon: '🌋', tier: 'bronze', funny: true,
        desc: 'Visit a lava planet. SPF 9000.',
        check: (s) => !!s.planetTypes?.lava,
    },
    {
        id: 'swamp_thing', name: 'Swamp Thing', icon: '🤢', tier: 'bronze', funny: true,
        desc: 'Dock on a toxic world. Gross.',
        check: (s) => !!s.planetTypes?.toxic,
    },
    {
        id: 'metalhead', name: 'Metalhead', icon: '⚙️', tier: 'bronze',
        desc: 'Visit a metal planet.',
        check: (s) => !!s.planetTypes?.metal,
    },
    {
        id: 'storm_chaser', name: 'Storm Chaser', icon: '⛈️', tier: 'bronze',
        desc: 'Enter a storm world.',
        check: (s) => !!s.planetTypes?.storm,
    },
    {
        id: 'gas_guzzler', name: 'Gas Guzzler', icon: '💨', tier: 'bronze', funny: true,
        desc: 'Visit a gas giant. Please don’t inhale.',
        check: (s) => !!s.planetTypes?.gas,
    },

    // ── Portals / void ──
    {
        id: 'black_hole_diet', name: 'Black Hole Diet', icon: '🕳️', tier: 'silver', funny: true,
        desc: 'Transit through black holes 10 times.',
        check: (s) => s.portalUses >= 10,
    },
    {
        id: 'void_tourist', name: 'Void Tourist', icon: '🎟️', tier: 'bronze', funny: true,
        desc: 'Enter a void pocket arena.',
        check: (s) => s.voidEntries >= 1,
    },
    {
        id: 'i_meant_to', name: 'I Meant To Do That', icon: '🏃', tier: 'silver', funny: true,
        desc: 'Escape a void arena without killing the boss.',
        check: (s) => s.voidEscapes >= 1,
    },
    {
        id: 'arena_champ', name: 'Arena Champ', icon: '🏆', tier: 'gold',
        desc: 'Defeat a void arena boss.',
        check: (s) => s.voidBossKills >= 1,
    },
    {
        id: 'duelist', name: 'Duelist', icon: '⚔️', tier: 'silver',
        desc: 'Call a duel battlefield (F).',
        check: (s) => s.duelsCalled >= 1,
    },

    // ── Trade / craft / gear ──
    {
        id: 'beacon_bum', name: 'Beacon Bum', icon: '📡', tier: 'bronze', funny: true,
        desc: 'Call a trade beacon mid-fight.',
        check: (s) => s.beaconsCalled >= 1,
    },
    {
        id: 'station_rat', name: 'Station Rat', icon: '🏪', tier: 'bronze', funny: true,
        desc: 'Buy or sell at a planetary station.',
        check: (s) => s.stationTrades >= 1,
    },
    {
        id: 'artefact_hunter', name: 'Artefact Hunter', icon: '✦', tier: 'silver',
        desc: 'Loot your first combat artefact.',
        check: (s) => s.artefactsFound >= 1,
    },
    {
        id: 'mutant_fashion', name: 'Mutant Fashion', icon: '🧬', tier: 'silver', funny: true,
        desc: 'Mutate a module. New look, who dis?',
        check: (s) => s.mutations >= 1,
    },
    {
        id: 'upgrade_addict', name: 'Upgrade Addict', icon: '⬆️', tier: 'gold',
        desc: 'Perform 10 gear upgrades.',
        check: (s) => s.upgrades >= 10,
    },
    {
        id: 'cargo_cult', name: 'Cargo Cult', icon: '📦', tier: 'silver', funny: true,
        desc: 'Fill every cargo slot at least once.',
        check: (s) => s.cargoFull >= 1,
    },
    {
        id: 'fuel_anxiety', name: 'Fuel Anxiety', icon: '⛽', tier: 'bronze', funny: true,
        desc: 'Drop below 10 fuel. Panic jumps loading…',
        check: (s) => s.lowFuel >= 1,
    },

    // ── Abilities / toys ──
    {
        id: 'oops_lasers', name: 'Oops All Lasers', icon: '🔴', tier: 'bronze', funny: true,
        desc: 'Pick the laser weapon ability.',
        check: (s) => !!s.abilities?.laser,
    },
    {
        id: 'orbiting_feelings', name: 'Orbiting My Feelings', icon: '🛡️', tier: 'bronze', funny: true,
        desc: 'Unlock orbital shields.',
        check: (s) => !!s.abilities?.orbit,
    },
    {
        id: 'clone_army', name: 'Clone Army', icon: '👥', tier: 'silver', funny: true,
        desc: 'Deploy combat clones.',
        check: (s) => s.clonesSpawned >= 1,
    },
    {
        id: 'geode_goblin', name: 'Geode Goblin', icon: '💠', tier: 'bronze', funny: true,
        desc: 'Pick up a Geometry Wars bomb geode.',
        check: (s) => s.geodesPicked >= 1,
    },
    {
        id: 'big_boom', name: 'Big Boom Energy', icon: '💥', tier: 'silver',
        desc: 'Detonate 5 bombs.',
        check: (s) => s.bombsUsed >= 5,
    },
    {
        id: 'magnet_personality', name: 'Magnet Personality', icon: '🧲', tier: 'bronze', funny: true,
        desc: 'Stack magnet range over 150.',
        check: (s) => s.maxMagnet >= 150,
    },
    {
        id: 'glass_cannon', name: 'Glass Cannon', icon: '🥂', tier: 'silver', funny: true,
        desc: 'Deal 40+ damage while under 80 max HP.',
        check: (s) => s.glassCannon >= 1,
    },
    {
        id: 'friendly_neighborhood', name: 'Wingman Warranty', icon: '🤝', tier: 'bronze',
        desc: 'Fight with an ally bot on the field.',
        check: (s) => s.botAssists >= 1,
    },
];

export const BADGE_BY_ID = Object.fromEntries(BADGE_DEFS.map((b) => [b.id, b]));

export const TIER_COLOR = {
    bronze: '#cd7f32',
    silver: '#c0c8d4',
    gold: '#ffd060',
    legend: '#ff66ee',
};

/** Default empty stats for progression */
export function emptyProgressStats() {
    return {
        kills: 0,
        bossKills: 0,
        deaths: 0,
        nearDeathSurvives: 0,
        pilotLevel: 1,
        pilotXp: 0,
        sectorsCleared: 0,
        crystalsEarned: 0,
        timesBroke: 0,
        hyperjumps: 0,
        planetsVisited: [],
        planetTypes: {},
        portalUses: 0,
        voidEntries: 0,
        voidEscapes: 0,
        voidBossKills: 0,
        duelsCalled: 0,
        beaconsCalled: 0,
        stationTrades: 0,
        artefactsFound: 0,
        mutations: 0,
        upgrades: 0,
        cargoFull: 0,
        lowFuel: 0,
        abilities: {},
        clonesSpawned: 0,
        geodesPicked: 0,
        bombsUsed: 0,
        maxMagnet: 0,
        glassCannon: 0,
        botAssists: 0,
        hitBySaucer: 0,
        killsByVariant: {},
    };
}
